import set from 'lodash/set';
import { nanoid } from 'nanoid/non-secure';

import { getNowISO } from './helper';
import { safeSessionStorage, safeGetUserTraits } from './trackingStorage';
import { convert } from './v4';
import { identify } from './v4.calls';
import psProperties from './v4.ps-properties';

// Prefix for storing campaign session IDs in sessionStorage
const CAMPAIGN_SESSION_PREFIX = 'perspective_campaign_session_';

// Tracks whether any non-homepage page has been seen in this session.
// Used to decide if a homepage arrival should regenerate the session.
const SEEN_NON_HOMEPAGE_PREFIX = 'perspective_seen_non_homepage_';

const conversionProperties = ['email', 'phone'];

export class TrackingSession {
    private sessionId: string;
    private hasSeenAnyNonHomepage: boolean;
    private visitorProperties: Partial<Record<string, string>>;
    private lastIdentifyData: Record<string, unknown> = {};

    constructor() {
        this.sessionId = '';
        this.hasSeenAnyNonHomepage = false;
        this.visitorProperties = {};
        this.lastIdentifyData = {};
    }

    static peek(campaignId: string): { sessionId: string; hasSeenAnyNonHomepage: boolean } | null {
        const sessionId = safeSessionStorage.getItem(`${CAMPAIGN_SESSION_PREFIX}${campaignId}`);

        if (!sessionId) return null;

        const hasSeenAnyNonHomepage =
            safeSessionStorage.getItem(`${SEEN_NON_HOMEPAGE_PREFIX}${sessionId}`) === '1';

        return { sessionId, hasSeenAnyNonHomepage };
    }

    restore(campaignId: string): boolean {
        const stored = safeSessionStorage.getItem(`${CAMPAIGN_SESSION_PREFIX}${campaignId}`);

        if (!stored) return false;

        this.sessionId = stored;
        this.hasSeenAnyNonHomepage =
            safeSessionStorage.getItem(`${SEEN_NON_HOMEPAGE_PREFIX}${this.sessionId}`) === '1';

        const value = safeGetUserTraits(`${stored}_user_traits`);

        if (value !== null) {
            try {
                const obj = JSON.parse(value);

                for (const [key, val] of Object.entries(obj)) {
                    if (val !== null && val !== undefined) {
                        this.visitorProperties[key] = String(val);
                    }
                }
            } catch (error) {
                console.error('Failed to parse user traits:', error);
            }
        }

        // Expose for E2E testing — stripped from production builds by Vite
        if (import.meta.env.DEV && typeof window !== 'undefined') {
            (window as any).__visitorProperties = this.visitorProperties;
        }

        return true;
    }

    create(campaignId: string): void {
        const urlSessionId = new URLSearchParams(window.location.search).get('sessionId');
        this.sessionId = urlSessionId || nanoid();
        safeSessionStorage.setItem(`${CAMPAIGN_SESSION_PREFIX}${campaignId}`, this.sessionId);

        // Expose for E2E testing — stripped from production builds by Vite
        if (import.meta.env.DEV && typeof window !== 'undefined') {
            (window as any).__visitorProperties = this.visitorProperties;
        }
    }

    // --- Session ID ---

    getSessionId(): string {
        return this.sessionId;
    }

    /**
     * Whether this session should be regenerated on homepage arrival.
     * Returns true when the user has visited any non-homepage page in this session,
     * meaning a homepage arrival signals a new funnel journey.
     */
    shouldRegenerateForHomepage(): boolean {
        if (!this.sessionId) return false;
        return this.hasSeenAnyNonHomepage;
    }

    // Called from Page.svelte after variant resolution to track which page is currently shown.
    setCurrentPage(_pageSlug: string, isHomePage: boolean): void {
        if (!isHomePage && !this.hasSeenAnyNonHomepage) {
            this.hasSeenAnyNonHomepage = true;
            safeSessionStorage.setItem(`${SEEN_NON_HOMEPAGE_PREFIX}${this.sessionId}`, '1');
        }
    }

    // --- Visitor Properties ---

    updateVisitorProperties(
        obj: { [key: string]: unknown },
        options?: { suppressConversion?: boolean },
    ): void {
        Object.keys(obj).forEach((key) => {
            set(this.visitorProperties, key, obj[key]);
        });

        if (!options?.suppressConversion) {
            this.triggerConversion();
        }

        set(this.visitorProperties, psProperties.lastSeenAt, getNowISO());

        identify(this.visitorProperties);
    }

    private triggerConversion(): void {
        conversionProperties.forEach((conversionProperty) => {
            if (
                this.visitorProperties[conversionProperty] &&
                !this.visitorProperties[psProperties.isConverted]
            ) {
                set(this.visitorProperties, psProperties.isConverted, true);
                set(this.visitorProperties, psProperties.convertedAt, getNowISO());
                convert();
            }
        });
    }

    triggerDeferredConversion(): void {
        this.triggerConversion();
        identify(this.visitorProperties);
    }

    getVisitorProperties(): Partial<Record<string, string>> {
        return this.visitorProperties;
    }

    // --- Identify Deduplication ---

    isIdentifyDuplicate(data: Record<string, unknown>): boolean {
        const isEqual = JSON.stringify(data) === JSON.stringify(this.lastIdentifyData);

        if (!isEqual) {
            this.lastIdentifyData = data;
        }
        return isEqual;
    }
}
